import {switchAnatomy as parts} from '@chakra-ui/anatomy';
import {useMultiStyleConfig, getToken} from '@chakra-ui/react';
import {createMultiStyleConfigHelpers, defineStyle} from '@chakra-ui/styled-system';
import type {ThemingProps} from '@chakra-ui/system';
import {cssVar, mode} from '@chakra-ui/theme-tools';

import {components as darkComponents} from '../../design-tokens/dark';
import {components} from '../../design-tokens/light';

// Links for @chakra-ui/theme@3.3.0

// Theme source
// @link https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/theme%403.3.0/packages/components/theme/src/components/switch.ts

// Component source
// @link https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/theme%403.3.0/packages/components/switch/src/switch.tsx

// Design
// https://www.figma.com/file/fUp4qrVCn5nngJirY3KJ6z/Aligned-Design-System?type=design&node-id=510-27001&mode=design&t=WdBxqgVaz41QXLb3-0

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(parts.keys);

const togglesPrimary = components.colors.components.toggles;
const togglesPrimaryDark = darkComponents.colors.components.toggles;

const $width = cssVar(`switch-track-width`);
const $height = cssVar(`switch-track-height`);
const $bg = cssVar(`switch-bg`);

const baseStyleThumb = defineStyle(props => {
  const bgDisabledVar = mode(
    getToken(`colors`, togglesPrimary[`icon-disabled`])(props.theme),
    getToken(`colors`, togglesPrimaryDark[`icon-disabled`])(props.theme),
  )(props);

  return {
    bg: `white`,
    _disabled: {
      bg: bgDisabledVar,
    },
    _checked: {
      _disabled: {
        bg: bgDisabledVar,
      },
    },
  };
});

const baseStyleTrack = defineStyle(props => {
  const boxShadowColor = mode(
    getToken(`colors`, togglesPrimary[`stroke-focus`])(props.theme),
    getToken(`colors`, togglesPrimaryDark[`stroke-focus`])(props.theme),
  )(props);

  const bgUncheckedVar = mode(
    getToken(`colors`, togglesPrimary[`background-unselected`])(props.theme),
    getToken(`colors`, togglesPrimaryDark[`background-unselected`])(props.theme),
  )(props);
  const bgCheckedVar = mode(
    getToken(`colors`, togglesPrimary[`background-selected`])(props.theme),
    getToken(`colors`, togglesPrimaryDark[`background-selected`])(props.theme),
  )(props);
  const bgDisabledVar = mode(
    getToken(`colors`, togglesPrimary[`background-disabled`])(props.theme),
    getToken(`colors`, togglesPrimaryDark[`background-disabled`])(props.theme),
  )(props);

  return {
    [$bg.variable]: bgUncheckedVar,
    _focusVisible: {
      boxShadow: `0 0 0 3px ${boxShadowColor}`,
    },
    _disabled: {
      opacity: 1,
    },
    _checked: {
      [$bg.variable]: bgCheckedVar,
      _disabled: {
        [$bg.variable]: bgDisabledVar,
      },
    },
  };
});

const baseStyle = definePartsStyle(props => ({
  track: baseStyleTrack(props),
  thumb: baseStyleThumb(props),
}));

const sizes = {
  md: definePartsStyle({
    container: {
      [$width.variable]: `36px`,
      [$height.variable]: `20px`,
    },
  }),
};

export const switchTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    //
  },
});

export const useSwitchStyleConfig = (props: ThemingProps) => {
  return useMultiStyleConfig(`Switch`, props ?? {});
};
